/**
 * ZON-275 — school news for the app: the Notifications feed and one school's
 * Recent news card. Read-only client for the API's /school-news routes.
 */
const API_BASE_URL: string = import.meta.env.VITE_API_URL || "";

function apiUrl(path: string): URL {
  const base = API_BASE_URL.replace(/\/$/, "");
  return new URL(`${base}${path}`, base ? undefined : window.location.origin);
}

export interface NewsResult {
  teamPlace: number | null;
  teamWon: boolean;
}

export interface NewsItem {
  id: string;
  feed: "news";
  kind: "alert" | "digest";
  type: string;
  teamId: string;
  schoolName: string | null;
  logoUrl: string | null;
  pipelineEntryId: string | null;
  title: string;
  summary: string | null;
  publishedAt: string;
  url: string;
  result: NewsResult | null;
  people: { name: string; role: string }[];
}

export interface FitSchool {
  teamId: string;
  schoolName: string | null;
  division: string | null;
  conference: string | null;
  city: string | null;
  state: string | null;
  gender: string | null;
  logoUrl: string | null;
  fitTier: "dream" | "realistic" | "safety" | null;
  headCoach: string | null;
  headCoachEmail: string | null;
  /** 0-100, and the one-line reason, as recorded when the school was recommended. */
  fitScore: number | null;
  reason: string | null;
}

export interface FitItem {
  id: string;
  feed: "fits";
  publishedAt: string;
  schools: FitSchool[];
}

export type NotificationItem = NewsItem | FitItem;
export type NewsFilter = "all" | "news" | "fits";

export interface NewsPage<T> {
  enabled: boolean;
  items: T[];
  nextCursor: string | null;
  /** Notifications feed, first page only: when the newest item was added (for the nav dot). */
  latestAddedAt?: string | null;
}

export const DISABLED_PAGE: NewsPage<never> = { enabled: false, items: [], nextCursor: null };

async function getJson<T>(url: URL, what: string): Promise<T> {
  const resp = await fetch(url.toString(), { method: "GET", credentials: "include" });
  if (!resp.ok) {
    const text = await resp.text().catch(() => "");
    throw new Error(text || `Failed to load ${what} (${resp.status})`);
  }
  return resp.json();
}

export async function fetchNotifications(
  params: { filter?: NewsFilter; before?: string; limit?: number } = {}
): Promise<NewsPage<NotificationItem>> {
  const url = apiUrl("/school-news/notifications");
  if (params.filter && params.filter !== "all") url.searchParams.set("filter", params.filter);
  if (params.before) url.searchParams.set("before", params.before);
  if (params.limit) url.searchParams.set("limit", String(params.limit));
  return getJson(url, "notifications");
}

export async function fetchTeamNews(
  teamId: string,
  params: { before?: string; limit?: number } = {}
): Promise<NewsPage<NewsItem>> {
  const url = apiUrl(`/school-news/teams/${encodeURIComponent(teamId)}`);
  if (params.before) url.searchParams.set("before", params.before);
  if (params.limit) url.searchParams.set("limit", String(params.limit));
  return getJson(url, "school news");
}
